/**
 * Wire protocol for meeting data channels (v4 — peer mesh with a
 * migrating host).
 *
 * One participant is the host: it owns the meeting server and relays
 * guest-to-guest traffic, and guest-to-guest peer invite codes are
 * negotiated over that host connection:
 *
 *   guest --plain broadcastable--> host --FROM{id,msg}--> other guests
 *   guest --TO{to,link-invite}---> host --FROM{id,link-invite}--> target guest
 *
 * Plain (unwrapped) content messages received by a guest come from the
 * host itself.
 *
 * The host role is not fixed. When the host leaves, the survivors elect a
 * successor (lowest participant id) which serves a fresh room and floods HOST_CLAIM across the
 * mesh; everyone else re-points their host link at it. `term` counts
 * migrations so a late or duplicate claim can't unseat a newer host —
 * highest term wins, lowest id breaks a tie.
 *
 * Guests need no account. The relays come from the backend's ICE list, and
 * every participant learns the MEETING SECRET in WELCOME — the lease the room
 * directory holds on the meeting's link — so whoever inherits the host role
 * can carry the meeting on.
 *
 * Every message is a JSON envelope: { v, t, ...payload }.
 * Unknown or malformed messages are dropped by parse().
 */

export const PROTOCOL_VERSION = 4;

export const MSG = Object.freeze({
  HELLO: 'hello', //             guest -> host: introduce self
  WELCOME: 'welcome', //         host -> guest: host identity + current roster
  PEER_JOINED: 'peer-joined', // host -> guests: someone joined
  PEER_LEFT: 'peer-left', //     host -> guests: someone left
  TO: 'to', //                   guest -> host: route a targeted peer message
  FROM: 'from', //               host -> guest: relayed message with sender id
  LINK_INVITE: 'link-invite', // guest -> guest via host: temp peer invite code
  LINK_HELLO: 'link-hello', //   guest -> guest direct: identify on temp link
  LINK_READY: 'link-ready', //   guest -> guest direct: temp link accepted
  STATE: 'state', //             name/media/hand state update
  CHAT: 'chat', //               chat message
  REACTION: 'reaction', //       emoji reaction
  CAPTION: 'caption', //         a spoken sentence, transcribed by the speaker's browser
  BYE: 'bye', //                 graceful leave
  HOST_CLAIM: 'host-claim', //   a survivor has taken over as host (flooded)
  WAITING: 'waiting', //         host -> guest: held in the waiting room until admitted
  DENIED: 'denied', //           host -> guest: turned away (denied, locked, passcode, removed)
  SETTINGS: 'settings', //       host -> guests: waiting room / lock / passcode changed
  MUTE: 'mute', //               host -> guest: switch your microphone off
  REMOVE: 'remove', //           host -> guest: you have been removed from the meeting
  STAFF: 'staff', //             host -> guests: which participants are team members (the host vouches)
  WAITING_LIST: 'waiting-list', // host -> team members: who is waiting to be admitted
  MOD: 'mod', //                 team member -> host: admit / deny / remove / mute / change settings
});

/** What a team member may ask the host to do for them (MOD.action). */
export const MOD_ACTION = Object.freeze({
  ADMIT: 'admit',
  DENY: 'deny',
  ADMIT_ALL: 'admit-all',
  REMOVE: 'remove',
  MUTE: 'mute',
  MUTE_ALL: 'mute-all',
  SETTINGS: 'settings',
});

/** Why a host turned a guest away (DENIED.reason). */
export const DENY_REASON = Object.freeze({
  DENIED: 'denied', //     the host said no in the waiting room
  LOCKED: 'locked', //     the meeting is locked: no new joins
  PASSCODE: 'passcode', // the meeting needs a passcode, and this one was missing or wrong
  REMOVED: 'removed', //   the host removed this participant (also sent to the one removed)
});

/**
 * What a meeting the host starts begins as: a CLOSED room — every guest waits
 * until the host admits them. The host can open it (guests join directly).
 */
export const DEFAULT_HOST_SETTINGS = Object.freeze({ waitingRoom: true, locked: false, passcode: '' });

/** Longest meeting passcode we carry. */
export const MAX_PASSCODE_CHARS = 64;

/**
 * The host's meeting-wide controls. `passcode` is empty when the meeting has
 * none; it travels only to participants already admitted (so any of them can
 * keep enforcing it should the host role move to them).
 */
export function normalizeSettings(settings) {
  const s = settings && typeof settings === 'object' ? settings : {};
  return {
    waitingRoom: !!s.waitingRoom,
    locked: !!s.locked,
    passcode: typeof s.passcode === 'string' ? s.passcode.trim().slice(0, MAX_PASSCODE_CHARS) : '',
  };
}

/** Message types the host relays to the other guests (fan-out). */
export const BROADCASTABLE = Object.freeze([
  MSG.STATE,
  MSG.CHAT,
  MSG.REACTION,
  MSG.CAPTION,
]);

/** Message types allowed inside a FROM envelope. */
const RELAYABLE = Object.freeze([...BROADCASTABLE, MSG.LINK_INVITE]);

/** Shape of the participant identity carried by hello / welcome / peer-joined. */
export function makeIdentity({ id, name, mic, cam, hand, presenting }) {
  return {
    id: String(id),
    name: sanitizeName(name),
    mic: !!mic,
    cam: !!cam,
    hand: !!hand,
    presenting: !!presenting,
  };
}

/** Longest secret a HELLO may carry (ours is a UUID, 36 chars). */
export const MAX_TOKEN_CHARS = 128;

/**
 * A guest's HELLO carries a per-session secret alongside its identity. The
 * host keeps it, and only a later HELLO for the same id that presents it
 * again is taken as that participant coming back on a fresh connection —
 * everyone learns everyone else's id from WELCOME and PEER_JOINED, so the
 * id alone would let any participant evict, and stand in for, any other.
 * The secret goes to the host and no further.
 */
export function makeHello(identity, token = '', passcode = '', ask = false) {
  const hello = { v: PROTOCOL_VERSION, t: MSG.HELLO, peer: identity, token: normalizeToken(token) };
  if (passcode) hello.passcode = normalizePasscode(passcode);
  // A guest waiting to be let in can ask again: the host is notified afresh.
  if (ask) hello.ask = true;
  return hello;
}

/**
 * The host's answer to a HELLO: who it is, who else is here, how many times
 * the meeting has changed hands — and the MEETING SECRET: what the room directory's lease on this
 * meeting's link is held under (lib/rooms.js). Every participant learns it
 * so that whoever inherits the host role can carry the lease on. It is as
 * confidential as the link itself: anyone in the meeting may end up
 * hosting it.
 */
export function makeWelcome(hostIdentity, peers, term = 0, secret = '', settings = null, staff = []) {
  return {
    v: PROTOCOL_VERSION,
    t: MSG.WELCOME,
    peer: hostIdentity,
    peers,
    term: normalizeTerm(term),
    secret: normalizeToken(secret),
    settings: normalizeSettings(settings),
    staff: normalizeIds(staff),
  };
}

/** Host -> guests: the participants who are team members. Only the host's word counts. */
export function makeStaff(ids) {
  return { v: PROTOCOL_VERSION, t: MSG.STAFF, ids: normalizeIds(ids) };
}

/** Host -> team members: who is waiting to be admitted. */
export function makeWaitingList(list) {
  return { v: PROTOCOL_VERSION, t: MSG.WAITING_LIST, list: normalizeWaitingList(list) };
}

/** Team member -> host: a moderation request. The host checks the sender is a team member it vouched for. */
export function makeMod(action, { id = '', settings = null } = {}) {
  const mod = { v: PROTOCOL_VERSION, t: MSG.MOD, action };
  if (id) mod.id = String(id);
  if (settings) mod.settings = settings;
  return mod;
}

/** Host -> guest: you are in the waiting room; media starts when you are admitted. */
export function makeWaiting() {
  return { v: PROTOCOL_VERSION, t: MSG.WAITING };
}

export function makeDenied(reason = DENY_REASON.DENIED) {
  const known = Object.values(DENY_REASON).includes(reason) ? reason : DENY_REASON.DENIED;
  return { v: PROTOCOL_VERSION, t: MSG.DENIED, reason: known };
}

export function makeSettings(settings) {
  return { v: PROTOCOL_VERSION, t: MSG.SETTINGS, settings: normalizeSettings(settings) };
}

export function makeMute() {
  return { v: PROTOCOL_VERSION, t: MSG.MUTE };
}

export function makeRemove() {
  return { v: PROTOCOL_VERSION, t: MSG.REMOVE };
}

/**
 * Announce that `hostId` is now the meeting's host, serving newcomers at
 * `invite`. Flooded over every link so it also reaches participants with
 * no direct link to the new host. `invite` may be empty: the successor
 * claims the role immediately and re-announces once its server is up.
 */
export function makeHostClaim({ hostId, term, invite }) {
  return {
    v: PROTOCOL_VERSION,
    t: MSG.HOST_CLAIM,
    hostId: String(hostId),
    term: normalizeTerm(term),
    invite: invite ? String(invite) : '',
  };
}

export function makePeerJoined(identity) {
  return { v: PROTOCOL_VERSION, t: MSG.PEER_JOINED, peer: identity };
}

export function makePeerLeft(id, reason = 'left') {
  return { v: PROTOCOL_VERSION, t: MSG.PEER_LEFT, id: String(id), reason: String(reason).slice(0, 200) };
}

export function makeLinkInvite(inviteCode) {
  return { v: PROTOCOL_VERSION, t: MSG.LINK_INVITE, inviteCode: String(inviteCode) };
}

export function makeLinkHello(identity) {
  return { v: PROTOCOL_VERSION, t: MSG.LINK_HELLO, peer: identity };
}

export function makeLinkReady(identity) {
  return { v: PROTOCOL_VERSION, t: MSG.LINK_READY, peer: identity };
}

export function makeTo(to, msg) {
  return { v: PROTOCOL_VERSION, t: MSG.TO, to: String(to), msg };
}

export function makeFrom(id, msg) {
  return { v: PROTOCOL_VERSION, t: MSG.FROM, id: String(id), msg };
}

export function makeState({ name, mic, cam, hand, presenting }) {
  return {
    v: PROTOCOL_VERSION, t: MSG.STATE, mic: !!mic, cam: !!cam, hand: !!hand, presenting: !!presenting,
    // Optional so older clients' state updates preserve the current name.
    ...(typeof name === 'string' ? { name: sanitizeName(name) } : {}),
  };
}

/**
 * A chat message. Its `id` lets a message be sent again over a rebuilt link
 * without anyone seeing it twice; peers on an older build drop it and show
 * the message as they always have.
 */
export function makeChat(text, ts, id) {
  return {
    v: PROTOCOL_VERSION,
    t: MSG.CHAT,
    text: String(text).slice(0, 4000),
    ts: ts ?? Date.now(),
    id: typeof id === 'string' && id ? id.slice(0, 64) : crypto.randomUUID(),
  };
}

/** One sentence the sender's browser heard them say. */
export function makeCaption(text, ts) {
  return { v: PROTOCOL_VERSION, t: MSG.CAPTION, text: String(text).slice(0, 1000), ts: ts ?? Date.now() };
}

export function makeReaction(emoji) {
  return { v: PROTOCOL_VERSION, t: MSG.REACTION, emoji: String(emoji).slice(0, 8) };
}

export function makeBye(reason = 'left') {
  return { v: PROTOCOL_VERSION, t: MSG.BYE, reason: String(reason).slice(0, 200) };
}

export function encode(msg) {
  return JSON.stringify(msg);
}

/** The names we publish media under, and the only ones we adopt. */
export const STREAM_KINDS = Object.freeze(['camera', 'screen']);

/**
 * Ceiling on an incoming frame, in characters.
 *
 * Every field the validator sees is bounded — names at 60, chat at 4000,
 * invite codes at 512 — now that SDP travels over the signaller rather than
 * through here. WELCOME is the largest thing left, and it grows only with
 * the roster, so this needs to clear a big meeting and nothing more.
 */
export const MAX_FRAME_CHARS = 65_536;

/**
 * Parse and validate an incoming raw message.
 * Returns the message object, or null if it should be dropped.
 */
export function parse(raw) {
  if (typeof raw !== 'string') return null;
  if (raw.length > MAX_FRAME_CHARS) return null; // dropped unread
  let msg;
  try {
    msg = JSON.parse(raw);
  } catch {
    return null;
  }
  return validate(msg, /* allowEnvelope */ true);
}

/** Validate an already-decoded message object. */
export function validate(msg, allowEnvelope = true) {
  if (!msg || typeof msg !== 'object' || Array.isArray(msg)) return null;
  if (msg.v !== PROTOCOL_VERSION) return null;

  switch (msg.t) {
    case MSG.HELLO:
      if (!isIdentity(msg.peer)) return null;
      return {
        v: msg.v,
        t: msg.t,
        peer: makeIdentity(msg.peer),
        token: normalizeToken(msg.token),
        passcode: normalizePasscode(msg.passcode),
        ask: msg.ask === true,
      };

    case MSG.LINK_HELLO:
    case MSG.LINK_READY:
    case MSG.PEER_JOINED:
      if (!isIdentity(msg.peer)) return null;
      return { v: msg.v, t: msg.t, peer: makeIdentity(msg.peer) };

    case MSG.WELCOME: {
      if (!isIdentity(msg.peer) || !Array.isArray(msg.peers)) return null;
      const peers = msg.peers.filter(isIdentity).map(makeIdentity);
      return {
        v: msg.v,
        t: msg.t,
        peer: makeIdentity(msg.peer),
        peers,
        term: normalizeTerm(msg.term),
        secret: normalizeToken(msg.secret),
        settings: normalizeSettings(msg.settings),
        staff: normalizeIds(msg.staff),
      };
    }

    case MSG.STAFF:
      return makeStaff(msg.ids);

    case MSG.WAITING_LIST:
      return makeWaitingList(msg.list);

    case MSG.MOD: {
      if (!Object.values(MOD_ACTION).includes(msg.action)) return null;
      const mod = { v: msg.v, t: msg.t, action: msg.action };
      if (typeof msg.id === 'string' && msg.id && msg.id.length <= 128) mod.id = msg.id;
      if ((msg.action === MOD_ACTION.ADMIT || msg.action === MOD_ACTION.DENY || msg.action === MOD_ACTION.REMOVE || msg.action === MOD_ACTION.MUTE) && !mod.id) return null;
      if (msg.action === MOD_ACTION.SETTINGS) mod.settings = normalizeSettings(msg.settings);
      return mod;
    }

    case MSG.WAITING:
    case MSG.MUTE:
    case MSG.REMOVE:
      return { v: msg.v, t: msg.t };

    case MSG.DENIED:
      return makeDenied(msg.reason);

    case MSG.SETTINGS:
      return makeSettings(msg.settings);

    case MSG.HOST_CLAIM: {
      if (typeof msg.hostId !== 'string' || !msg.hostId || msg.hostId.length > 128) return null;
      if (typeof msg.invite !== 'string' || msg.invite.length > 512) return null;
      return { v: msg.v, t: msg.t, hostId: msg.hostId, term: normalizeTerm(msg.term), invite: msg.invite };
    }

    case MSG.PEER_LEFT:
      if (typeof msg.id !== 'string' || !msg.id) return null;
      return {
        v: msg.v,
        t: msg.t,
        id: msg.id,
        reason: typeof msg.reason === 'string' ? msg.reason.slice(0, 200) : 'left',
      };

    case MSG.TO: {
      if (!allowEnvelope || typeof msg.to !== 'string' || !msg.to) return null;
      const inner = validate(msg.msg, false);
      if (!inner || inner.t !== MSG.LINK_INVITE) return null;
      return { v: msg.v, t: msg.t, to: msg.to, msg: inner };
    }

    case MSG.FROM: {
      if (!allowEnvelope || typeof msg.id !== 'string' || !msg.id) return null;
      const inner = validate(msg.msg, false);
      if (!inner || !RELAYABLE.includes(inner.t)) return null;
      return { v: msg.v, t: msg.t, id: msg.id, msg: inner };
    }

    case MSG.STATE:
      return makeState(msg);

    case MSG.CHAT: {
      if (typeof msg.text !== 'string' || !msg.text.trim()) return null;
      const chat = { v: msg.v, t: msg.t, text: msg.text.slice(0, 4000), ts: Number(msg.ts) || Date.now() };
      if (typeof msg.id === 'string' && msg.id && msg.id.length <= 64) chat.id = msg.id;
      return chat;
    }

    case MSG.CAPTION:
      if (typeof msg.text !== 'string' || !msg.text.trim()) return null;
      return { v: msg.v, t: msg.t, text: msg.text.slice(0, 1000), ts: Number(msg.ts) || Date.now() };

    case MSG.REACTION:
      if (typeof msg.emoji !== 'string' || !msg.emoji) return null;
      return { v: msg.v, t: msg.t, emoji: msg.emoji.slice(0, 8) };

    case MSG.LINK_INVITE:
      if (typeof msg.inviteCode !== 'string' || !msg.inviteCode || msg.inviteCode.length > 512) return null;
      return { v: msg.v, t: msg.t, inviteCode: msg.inviteCode };

    case MSG.BYE:
      return { v: msg.v, t: msg.t, reason: typeof msg.reason === 'string' ? msg.reason.slice(0, 200) : 'left' };

    default:
      return null;
  }
}

/** A bounded list of participant ids. */
function normalizeIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.filter((id) => typeof id === 'string' && id && id.length <= 128).slice(0, 256);
}

function normalizeWaitingList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((w) => w && typeof w.id === 'string' && w.id && w.id.length <= 128)
    .slice(0, 100)
    .map((w) => ({ id: w.id, name: sanitizeName(w.name) }));
}

/** A HELLO secret is a bounded string; anything else reads as none. */
function normalizeToken(token) {
  return typeof token === 'string' && token.length <= MAX_TOKEN_CHARS ? token : '';
}

/** A HELLO passcode is a bounded string; anything else reads as none. */
function normalizePasscode(passcode) {
  return typeof passcode === 'string' ? passcode.trim().slice(0, MAX_PASSCODE_CHARS) : '';
}

/** Migration terms are non-negative integers; anything else reads as 0. */
function normalizeTerm(term) {
  const n = Number(term);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function isIdentity(peer) {
  return (
    peer &&
    typeof peer === 'object' &&
    typeof peer.id === 'string' &&
    peer.id.length > 0 &&
    peer.id.length <= 128 &&
    typeof peer.name === 'string'
  );
}

/** Trim, collapse whitespace, and bound the length of display names. */
export function sanitizeName(name) {
  const cleaned = String(name ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
  return cleaned || 'Guest';
}
